import { ColumnOption as Metric } from "@/types";
import { useRef, useMemo } from "react";
import SelectedMetrics from "./components/SelectedMetrics";
import { MetricsDropdown } from "./components/MetricsDropdown";
import { cn } from "@/lib/utils";
import { RiArrowDownSLine } from "@remixicon/react";
import {
  SqTooltip,
  SqTooltipContent,
  SqTooltipProvider,
  SqTooltipTrigger,
} from "@/components/squircle-ui/tooltip";
import { IconDatabase } from "@tabler/icons-react";

interface Props {
  metricsOptions: Metric[];
  selectedMetrics: Metric[];
  onMetricChange: (metrics: Metric[]) => void;
  trigger?: React.ReactNode;
  renderingStyle?: "single_select" | "multiselect" | "table_metrics_bar";
  className?: string;
  // Single select specific props
  defaultMetric?: string;
  onSetDefault?: (value: string) => void;
  // Color function to get color for a metric
  getMetricColor?: (metric: Metric, index: number) => string;
  // Function to determine if a metric should be disabled
  isMetricDisabled?: (metric: Metric) => boolean;
  // Sort metrics alphabetically
  sortAlphabetically?: boolean;
}
const Metrics = ({
  selectedMetrics,
  onMetricChange,
  metricsOptions,
  trigger,
  className = "",
  renderingStyle = "table_metrics_bar",
  defaultMetric,
  onSetDefault,
  getMetricColor,
  isMetricDisabled,
  sortAlphabetically = false,
}: Props) => {
  const metricsContainerRef = useRef<HTMLDivElement>(null);

  // Create default trigger for single select mode - must be before conditionals
  const DefaultSingleSelectTrigger = useMemo(() => {
    const selectedLabel = selectedMetrics[0]?.label || "Select metric";
    return (
      <SqTooltipProvider>
        <SqTooltip>
          <SqTooltipTrigger>
            <div
              className={`flex items-center overflow-hidden justify-between w-full cursor-pointer`}
            >
              {selectedMetrics?.[0]?.sub_type === "EXTERNAL" && (
                <IconDatabase size={14} className={`text-[#02185999] shrink-0 mr-1`} />
              )}
              <span className="text-brand-primary-new/80 text-[11px] truncate translate-y-[1px] max-w-[160px]">
                {selectedLabel}
              </span>
              {selectedMetrics?.[0]?.show_platforms && (
                <span className="text-[#FF4500] shrink-0">*</span>
              )}
              <RiArrowDownSLine size={14} color="#9aa3bd" className="ml-[4px] shrink-0" />
            </div>
          </SqTooltipTrigger>
          {selectedMetrics?.[0]?.show_platforms &&
            selectedMetrics?.[0]?.platforms &&
            selectedMetrics?.[0]?.platforms.length > 0 && (
              <SqTooltipContent
                portal={true}
                side="bottom"
                align="start"
                alignOffset={20}
              >
                <p>
                  {selectedMetrics[0]?.label}
                  <br />
                  Available for{" "}
                  {selectedMetrics[0]?.platforms
                    .map(
                      (platform) =>
                        platform.charAt(0).toUpperCase() +
                        platform.slice(1).toLowerCase(),
                    )
                    .join(", ")}
                </p>
              </SqTooltipContent>
            )}
        </SqTooltip>
      </SqTooltipProvider>
    );
  }, [selectedMetrics]);

  const handleMetricChange = (metrics: Metric[]) => {
    const previousMetricsCount = selectedMetrics?.length || 0;
    onMetricChange?.(metrics);
    // Scroll to right if metrics were added
    if (metrics.length > previousMetricsCount) {
      setTimeout(() => {
        if (metricsContainerRef.current) {
          metricsContainerRef.current.scrollTo({
            left: metricsContainerRef.current.scrollWidth,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  };

  if (renderingStyle === "table_metrics_bar")
    return (
      <div
        className={cn(
          `flex items-center gap-1 relative squircle squircle-smooth-[1] squircle-radius-[8px] squircle-mask squircle-fill-[white] before:absolute before:inset-0 before:squircle before:squircle-radius-[8px] before:squircle-smooth-[1] before:squircle-outline-[1px] before:squircle-outline-color-[#E7E8E9] before:pointer-events-none p-1 overflow-hidden`,
          className,
        )}
      >
        <div
          ref={metricsContainerRef}
          className="flex-1 min-w-0 overflow-x-auto scrollbar-hide"
        >
          <SelectedMetrics
            selectedMetrics={selectedMetrics}
            onMetricChange={handleMetricChange}
          />
        </div>
        <MetricsDropdown
          metricsOptions={metricsOptions}
          selectedMetrics={selectedMetrics}
          onMetricsChange={handleMetricChange}
          getMetricColor={getMetricColor}
          isMetricDisabled={isMetricDisabled}
          sortAlphabetically={sortAlphabetically}
        />
      </div>
    );

  // Single select mode - use MetricsDropdown with single select
  if (renderingStyle === "single_select") {
    return (
      <MetricsDropdown
        metricsOptions={metricsOptions}
        selectedMetrics={selectedMetrics}
        onMetricsChange={onMetricChange}
        trigger={trigger || DefaultSingleSelectTrigger}
        className={className}
        singleSelect={true}
        defaultMetric={defaultMetric}
        onSetDefault={onSetDefault}
        getMetricColor={getMetricColor}
        isMetricDisabled={isMetricDisabled}
        sortAlphabetically={sortAlphabetically}
      />
    );
  }

  return (
    <MetricsDropdown
      metricsOptions={metricsOptions}
      selectedMetrics={selectedMetrics}
      onMetricsChange={handleMetricChange}
      trigger={trigger}
      className={className}
      getMetricColor={getMetricColor}
      isMetricDisabled={isMetricDisabled}
      sortAlphabetically={sortAlphabetically}
    />
  );
};

export default Metrics;
